// Drawer admin para crear/editar un quiz: título + lista de preguntas con editor por tipo.
// Estrategia: cargar el quiz al abrir, editar en memoria, PUT idempotente al guardar.

import { useEffect, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { ApiError } from '@/services/api';
import {
  adminGetQuiz,
  adminReplaceQuestions,
  adminUpdateQuizTitle,
  type AdminQuestion,
  type AnswerType,
  type MatrixMatch,
} from '@/services/quizzes';
import styles from './QuizEditorDrawer.module.scss';

interface QuizEditorDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quizId: number | null;
  onSaved: () => void;
}

const TYPE_LABELS: Record<AnswerType, string> = {
  single: 'Elige 1',
  multiple: 'Elige varias',
  sort_answer: 'Ordena',
  matrix_sort_answer: 'Empareja',
  cloze_answer: 'Completa huecos',
};

function emptyPayloadFor(type: AnswerType): unknown {
  switch (type) {
    case 'single':
    case 'multiple':
      return { options: [{ id: 'a', text: '', is_correct: false }, { id: 'b', text: '', is_correct: false }] };
    case 'sort_answer':
      return { items: [{ id: 'a', text: '', correct_position: 1 }, { id: 'b', text: '', correct_position: 2 }] };
    case 'matrix_sort_answer':
      return {
        left: [{ id: 'l1', text: '' }, { id: 'l2', text: '' }],
        right: [{ id: 'r1', text: '' }, { id: 'r2', text: '' }],
        matches: [{ left_id: 'l1', right_id: 'r1' }, { left_id: 'l2', right_id: 'r2' }],
      };
    case 'cloze_answer':
      return { template: 'Hola {0}, ¿cómo {1}?', blanks: [{ accepted: [''] }, { accepted: [''] }] };
  }
}

function emptyQuestion(position: number): AdminQuestion {
  return {
    position,
    question_text: '',
    answer_type: 'single',
    points: 1,
    payload: emptyPayloadFor('single'),
  };
}

export function QuizEditorDrawer({ open, onOpenChange, quizId, onSaved }: QuizEditorDrawerProps) {
  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState<AdminQuestion[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !quizId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await adminGetQuiz(quizId);
        if (cancelled) return;
        setTitle(r.quiz.title);
        setQuestions(r.questions);
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error al cargar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, quizId]);

  function updateQuestion(idx: number, patch: Partial<AdminQuestion>) {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  }

  function changeType(idx: number, newType: AnswerType) {
    updateQuestion(idx, { answer_type: newType, payload: emptyPayloadFor(newType) });
  }

  function addQuestion() {
    setQuestions((prev) => [...prev, emptyQuestion(prev.length)]);
  }

  function removeQuestion(idx: number) {
    setQuestions((prev) => prev.filter((_, i) => i !== idx).map((q, i) => ({ ...q, position: i })));
  }

  async function save() {
    if (!quizId) return;
    setBusy(true);
    setError(null);
    try {
      await adminUpdateQuizTitle(quizId, title);
      await adminReplaceQuestions(
        quizId,
        questions.map((q, i) => ({ ...q, position: i }))
      );
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error al guardar');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.drawer} aria-describedby={undefined}>
          <header className={styles.header}>
            <Dialog.Title className={styles.title}>Editar quiz</Dialog.Title>
            <Dialog.Close asChild>
              <button className={styles.closeBtn} aria-label="Cerrar">
                <Icon name="x" size={20} />
              </button>
            </Dialog.Close>
          </header>

          <div className={styles.body}>
            {loading ? (
              <p>Cargando…</p>
            ) : (
              <>
                <label className={styles.field}>
                  <span>Título del quiz</span>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} />
                </label>

                {questions.map((q, idx) => (
                  <div key={idx} className={styles.questionCard}>
                    <header className={styles.questionHeader}>
                      <div className={styles.questionMeta}>
                        <strong>Pregunta {idx + 1}</strong>
                        <select
                          value={q.answer_type}
                          onChange={(e) => changeType(idx, e.target.value as AnswerType)}
                        >
                          {(Object.keys(TYPE_LABELS) as AnswerType[]).map((t) => (
                            <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                          ))}
                        </select>
                        <label>
                          puntos:
                          <input
                            type="number"
                            min={1}
                            max={100}
                            value={q.points}
                            onChange={(e) => updateQuestion(idx, { points: Number(e.target.value) || 1 })}
                            style={{ width: 50, padding: 4 }}
                          />
                        </label>
                      </div>
                      <button className={styles.removeBtn} onClick={() => removeQuestion(idx)} aria-label="Eliminar pregunta">
                        <Icon name="trash" size={14} />
                      </button>
                    </header>
                    <label className={styles.field}>
                      <span>Enunciado</span>
                      <textarea
                        rows={2}
                        value={q.question_text}
                        onChange={(e) => updateQuestion(idx, { question_text: e.target.value })}
                      />
                    </label>
                    <QuestionPayloadEditor
                      type={q.answer_type}
                      payload={q.payload}
                      onChange={(p) => updateQuestion(idx, { payload: p })}
                    />
                  </div>
                ))}

                <div className={styles.addRow}>
                  <Button variant="secondary" onClick={addQuestion}>
                    <Icon name="plus" size={14} /> Añadir pregunta
                  </Button>
                </div>

                <p className={styles.helperNote}>
                  Guardar reemplaza todas las preguntas del quiz. Los ID internos de opciones son arbitrarios pero deben ser únicos dentro de cada pregunta.
                </p>
              </>
            )}
          </div>

          {error && <p className={styles.error}>{error}</p>}

          <footer className={styles.footer}>
            <span style={{ fontSize: 12, color: '#666' }}>
              {questions.length} pregunta{questions.length === 1 ? '' : 's'}
            </span>
            <Button variant="primary" disabled={busy || loading} onClick={() => void save()}>
              {busy ? 'Guardando…' : 'Guardar quiz'}
            </Button>
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// --- Editor por tipo ---

interface QuestionPayloadEditorProps {
  type: AnswerType;
  payload: unknown;
  onChange: (p: unknown) => void;
}

function QuestionPayloadEditor({ type, payload, onChange }: QuestionPayloadEditorProps) {
  switch (type) {
    case 'single':
    case 'multiple':
      return <SingleMultipleEditor type={type} payload={payload as { options: Option[] }} onChange={onChange} />;
    case 'sort_answer':
      return <SortEditor payload={payload as { items: SortItem[] }} onChange={onChange} />;
    case 'matrix_sort_answer':
      return <MatrixSortEditor payload={payload as MatrixPayload} onChange={onChange} />;
    case 'cloze_answer':
      return <ClozeEditor payload={payload as ClozeEditorPayload} onChange={onChange} />;
  }
}

interface Option { id: string; text: string; is_correct: boolean }
interface SortItem { id: string; text: string; correct_position: number }
interface MatrixPayload {
  left: { id: string; text: string }[];
  right: { id: string; text: string }[];
  matches: MatrixMatch[];
}
interface ClozeEditorPayload {
  template: string;
  blanks: { accepted: string[] }[];
}

function SingleMultipleEditor({
  type, payload, onChange,
}: { type: 'single' | 'multiple'; payload: { options: Option[] }; onChange: (p: unknown) => void }) {
  function update(idx: number, patch: Partial<Option>) {
    onChange({ options: payload.options.map((o, i) => (i === idx ? { ...o, ...patch } : o)) });
  }
  function add() {
    const nextId = String.fromCharCode(97 + payload.options.length);
    onChange({ options: [...payload.options, { id: nextId, text: '', is_correct: false }] });
  }
  function remove(idx: number) {
    onChange({ options: payload.options.filter((_, i) => i !== idx) });
  }
  function setCorrect(idx: number, val: boolean) {
    if (type === 'single' && val) {
      onChange({ options: payload.options.map((o, i) => ({ ...o, is_correct: i === idx })) });
    } else {
      update(idx, { is_correct: val });
    }
  }
  return (
    <>
      {payload.options.map((o, idx) => (
        <div key={idx} className={styles.optionRow}>
          <input
            type="text"
            placeholder={`Opción ${idx + 1}`}
            value={o.text}
            onChange={(e) => update(idx, { text: e.target.value })}
          />
          <label>
            <input
              type={type === 'single' ? 'radio' : 'checkbox'}
              name="correct"
              checked={o.is_correct}
              onChange={(e) => setCorrect(idx, e.target.checked)}
            />
            correcta
          </label>
          <button className={styles.removeBtn} onClick={() => remove(idx)} aria-label="Quitar opción">
            <Icon name="x" size={14} />
          </button>
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={add}>
        <Icon name="plus" size={12} /> Añadir opción
      </Button>
    </>
  );
}

function SortEditor({
  payload, onChange,
}: { payload: { items: SortItem[] }; onChange: (p: unknown) => void }) {
  function update(idx: number, patch: Partial<SortItem>) {
    onChange({ items: payload.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)) });
  }
  function add() {
    const nextId = String.fromCharCode(97 + payload.items.length);
    onChange({ items: [...payload.items, { id: nextId, text: '', correct_position: payload.items.length + 1 }] });
  }
  function remove(idx: number) {
    onChange({ items: payload.items.filter((_, i) => i !== idx) });
  }
  return (
    <>
      {payload.items.map((it, idx) => (
        <div key={idx} className={styles.optionRow}>
          <input type="text" placeholder={`Item ${idx + 1}`} value={it.text} onChange={(e) => update(idx, { text: e.target.value })} />
          <label>
            pos:
            <input
              type="number"
              min={1}
              value={it.correct_position}
              onChange={(e) => update(idx, { correct_position: Number(e.target.value) || 1 })}
            />
          </label>
          <button className={styles.removeBtn} onClick={() => remove(idx)} aria-label="Quitar">
            <Icon name="x" size={14} />
          </button>
        </div>
      ))}
      <Button variant="ghost" size="sm" onClick={add}>
        <Icon name="plus" size={12} /> Añadir item
      </Button>
    </>
  );
}

function MatrixSortEditor({
  payload, onChange,
}: { payload: MatrixPayload; onChange: (p: unknown) => void }) {
  function setLeft(idx: number, text: string) {
    onChange({ ...payload, left: payload.left.map((l, i) => (i === idx ? { ...l, text } : l)) });
  }
  function setRight(idx: number, text: string) {
    onChange({ ...payload, right: payload.right.map((r, i) => (i === idx ? { ...r, text } : r)) });
  }
  function setMatch(leftId: string, rightId: string) {
    const filtered = payload.matches.filter((m) => m.left_id !== leftId);
    if (rightId) filtered.push({ left_id: leftId, right_id: rightId });
    onChange({ ...payload, matches: filtered });
  }
  return (
    <>
      <p className={styles.helperNote}>Columna izquierda (concepto) → columna derecha (definición). Define el emparejamiento correcto en cada fila.</p>
      {payload.left.map((leftItem, idx) => {
        const selected = payload.matches.find((m) => m.left_id === leftItem.id)?.right_id ?? '';
        return (
          <div key={leftItem.id} className={styles.optionRow}>
            <input
              type="text"
              placeholder={`Izquierda ${idx + 1}`}
              value={leftItem.text}
              onChange={(e) => setLeft(idx, e.target.value)}
            />
            <span>→</span>
            <select value={selected} onChange={(e) => setMatch(leftItem.id, e.target.value)}>
              <option value="">— elige —</option>
              {payload.right.map((r) => (
                <option key={r.id} value={r.id}>{r.text || r.id}</option>
              ))}
            </select>
          </div>
        );
      })}
      <p className={styles.helperNote}>Textos de la columna derecha:</p>
      {payload.right.map((r, idx) => (
        <div key={r.id} className={styles.optionRow}>
          <input
            type="text"
            placeholder={`Derecha ${idx + 1}`}
            value={r.text}
            onChange={(e) => setRight(idx, e.target.value)}
          />
        </div>
      ))}
    </>
  );
}

function ClozeEditor({
  payload, onChange,
}: { payload: ClozeEditorPayload; onChange: (p: unknown) => void }) {
  // Cuenta los {N} en el template y ajusta blanks proporcionalmente.
  function setTemplate(template: string) {
    const matches = template.match(/\{(\d+)\}/g) ?? [];
    const blankCount = matches.length;
    const blanks = Array.from({ length: blankCount }, (_, i) => payload.blanks[i] ?? { accepted: [''] });
    onChange({ template, blanks });
  }
  function setAccepted(idx: number, str: string) {
    const accepted = str.split(',').map((s) => s.trim()).filter(Boolean);
    onChange({
      ...payload,
      blanks: payload.blanks.map((b, i) => (i === idx ? { accepted: accepted.length ? accepted : [''] } : b)),
    });
  }
  return (
    <>
      <label className={styles.field}>
        <span>Template (usa {'{0}'}, {'{1}'}… donde van los huecos)</span>
        <textarea rows={2} value={payload.template} onChange={(e) => setTemplate(e.target.value)} />
      </label>
      {payload.blanks.map((b, idx) => (
        <div key={idx} className={styles.optionRow}>
          <span style={{ minWidth: 70 }}>Hueco {idx}:</span>
          <input
            type="text"
            placeholder="respuesta1, alternativa2"
            value={b.accepted.join(', ')}
            onChange={(e) => setAccepted(idx, e.target.value)}
          />
        </div>
      ))}
      <p className={styles.helperNote}>Separa con comas las respuestas aceptadas (insensible a mayúsculas y espacios).</p>
    </>
  );
}
